import { escapeHtml } from "../blocks/factories";
import { displayLabel } from "../../../utils/displayLabel";
import { parseLegacySocialLinks, SOCIAL_PLACEMENTS } from "../socialLinks";
import { makeSocialIconsBlock } from "../blocks/socialIconsFactory";
import { normalizeFooterPayload } from "../utils/footerPayload";
import sanitizeEditorHtml from '../sanitizeEditorHtml';

export default function buildEmailHtml({ bodyHtml, subject, outerBg = "#F4E9D6", cardBg = "#ffffff", contentWidth = "700px", fontFamily = "Georgia, serif", fontSize = "16px", textColor = "#111111", accentColor = "#541675", blockStyles, preheaderText, footer }) {
  contentWidth = `${Math.min(1000, Math.max(320, parseInt(contentWidth, 10) || 700))}px`;
  const safeBody = typeof document !== 'undefined' ? sanitizeEditorHtml(bodyHtml) : bodyHtml || '';
  const normalizedFooter = normalizeFooterPayload(footer);
  if (typeof document !== 'undefined') normalizedFooter.html = sanitizeEditorHtml(normalizedFooter.html);
  const footerSocialLinks = normalizedFooter.socialLinks || {};
  const footerSocialMarkup = Object.keys(footerSocialLinks).length
    ? makeSocialIconsBlock(footerSocialLinks, SOCIAL_PLACEMENTS.FOOTER)
    : "";

  const tempDiv = typeof document !== "undefined" ? document.createElement("div") : null;
  let processedBody = safeBody;
  let brandingHtml = "";

  if (tempDiv) {
    tempDiv.innerHTML = safeBody;

    const branding = [...tempDiv.querySelectorAll('[data-newsletter-branding]')];
    const brandingImage = branding[0]?.querySelector('img');
    if (brandingImage && /^https:\/\//i.test(brandingImage.getAttribute('src') || '')) {
      const width = Math.min(1000, Math.max(320, parseInt(contentWidth, 10) || 700));
      brandingHtml = `<tr><td style="padding:0;"><img src="${escapeHtml(brandingImage.getAttribute('src'))}" alt="${escapeHtml(brandingImage.alt || '')}" width="${width}" style="display:block;width:100%;max-width:${width}px;height:auto;border:0;margin:0;" /></td></tr>`;
    }
    branding.forEach(el => el.remove());

    const headerBlocks = Array.from(tempDiv.querySelectorAll('[data-bc-section="header"]'));
    const footerBlocks = Array.from(tempDiv.querySelectorAll('[data-bc-section="footer"]'));
    const bodyRoot = tempDiv.querySelector("#bc-section-root, #ml-root") || tempDiv;

    [...headerBlocks].reverse().forEach((node) => bodyRoot.insertBefore(node, bodyRoot.firstChild));

    // Keep at most one legacy footer block and force it to the end of body content.
    let legacyFooterHtml = "";
    if (footerBlocks.length) {
      legacyFooterHtml = footerBlocks[0]?.innerHTML || "";
      footerBlocks.forEach((node) => node.remove());
    }

    if (legacyFooterHtml && !(normalizedFooter.enabled && normalizedFooter.html)) {
      const legacyFooterWrap = document.createElement("div");
      legacyFooterWrap.innerHTML = legacyFooterHtml;
      bodyRoot.appendChild(legacyFooterWrap);
    }

    // STEP 1: Convert data-link images to <a> tags FIRST (before stripping data-* attrs)
    tempDiv.querySelectorAll("img[data-link]").forEach((img) => {
      const link = img.getAttribute("data-link");
      if (!link) return;
      const existingAnchor = img.closest("a");
      if (existingAnchor) { existingAnchor.href = link; img.removeAttribute("data-link"); return; }
      const anchor = document.createElement("a");
      anchor.href = link;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      img.removeAttribute("data-link");
      img.parentNode.insertBefore(anchor, img);
      anchor.appendChild(img);
    });


    // STEP 1b: Migrate legacy social icon markup into parsed link data when possible.
    // Fallback behavior: if parsing fails, leave legacy markup unchanged to avoid data loss.
    tempDiv.querySelectorAll("[data-social-icons]").forEach((socialContainer) => {
      const { links, parseFailed } = parseLegacySocialLinks(socialContainer);
      if (parseFailed || !links) return;

      const placement = socialContainer.getAttribute("data-social-placement") || SOCIAL_PLACEMENTS.BODY_BLOCK;
      const canonicalBlock = makeSocialIconsBlock(links, placement);
      const wrap = document.createElement("div");
      wrap.innerHTML = canonicalBlock;
      const canonicalSocial = wrap.querySelector("[data-social-icons]");
      if (canonicalSocial) socialContainer.innerHTML = canonicalSocial.innerHTML;
    });

    // STEP 2: Strip editor-only UI elements (edit buttons, drag handles, quick actions, labels, hints, empty placeholders)
    tempDiv.querySelectorAll("[data-bc-settings], [data-bc-drag], [data-bc-lock], [data-bc-quick-actions], [data-bc-label], [data-bc-hint], [data-image-slot], [data-bc-inline-actions], [data-bc-open-advanced], [data-bc-advanced]").forEach((el) => el.remove());
    tempDiv.querySelectorAll("button, [role='button']").forEach((el) => {
      const title = (el.getAttribute("title") || "").toLowerCase();
      const text = (el.textContent || "").trim().toLowerCase();
      if (title.includes("advanced") || text === "advanced") el.remove();
    });
    // Also strip old "Click to change" hints that lack the data-bc-hint attribute
    tempDiv.querySelectorAll("[data-book-cover] div[contenteditable='false']").forEach((el) => {
      if (el.textContent.trim() === "Click to change") el.remove();
    });

    // STEP 3: Strip interactive attributes (email clients block JS)
    tempDiv.querySelectorAll("[onclick]").forEach((el) => el.removeAttribute("onclick"));
    tempDiv.querySelectorAll("[contenteditable]").forEach((el) => el.removeAttribute("contenteditable"));
    tempDiv.querySelectorAll("[draggable]").forEach((el) => el.removeAttribute("draggable"));

    // Resolve overrides while editor identifiers are still available.
    tempDiv.querySelectorAll('[data-bc-id]').forEach((el) => {
      const overrides = blockStyles?.[el.getAttribute('data-bc-id')] || {};
      const properties = { backgroundColor: 'backgroundColor', textColor: 'color', padding: 'padding', fontFamily: 'fontFamily', fontSize: 'fontSize', borderRadius: 'borderRadius' };
      Object.entries(properties).forEach(([key, property]) => {
        if (overrides[key] != null) el.style[property] = overrides[key];
      });
      if (overrides.buttonTextColor) el.querySelectorAll('[data-bc-button]').forEach((button) => { button.style.color = overrides.buttonTextColor; });
      if (overrides.accentColor) el.querySelectorAll('[data-bc-accent]').forEach((accent) => { accent.style.backgroundColor = overrides.accentColor; });
    });
    // Preserve semantic formatting even in clients that remove head styles.
    [['b, strong', 'fontWeight', 'bold'], ['i, em', 'fontStyle', 'italic'], ['u', 'textDecoration', 'underline']].forEach(([selector, property, value]) => {
      tempDiv.querySelectorAll(selector).forEach((el) => { if (!el.style[property]) el.style[property] = value; });
    });

    // STEP 4: Strip remaining data-bc-* attributes (editor metadata)
    tempDiv.querySelectorAll("*").forEach((el) => {
      [...el.attributes].forEach((attr) => {
        if (attr.name.startsWith("data-bc-")) {
          el.removeAttribute(attr.name);
        }
      });
    });

    // STEP 5: Convert book/product blocks to table-based email-safe layout
    tempDiv.querySelectorAll("[data-book-layout]").forEach((bookBlock) => {
      bookBlock.querySelectorAll("h1, h2, h3, h4").forEach((heading) => {
        const walker = heading.ownerDocument.createTreeWalker(heading, 4);
        while (walker.nextNode()) walker.currentNode.textContent = displayLabel(walker.currentNode.textContent);
      });
      const coverWrap = bookBlock.querySelector("[data-book-cover]");
      const textWrap = [...bookBlock.children].find(el => el !== coverWrap && el.querySelector("h1,h2,h3,h4,p"));
      if (!coverWrap || !textWrap) return;

      const imageMarkup = coverWrap.innerHTML || "";
      const textMarkup = textWrap.innerHTML || "";
      const blockLayout = bookBlock.getAttribute("data-book-layout") === "right" ? "right" : "left";

      const border = bookBlock.getAttribute("data-book-border") === "true";
      // Replace editor flex geometry completely; email clients need real columns.
      bookBlock.style.cssText = "display:block;margin:20px 0;padding:0;border:0;";
      const coverCell = `<td class="book-cover-cell" width="32%" align="${blockLayout === "right" ? "right" : "left"}" valign="top" style="width:32%;padding:0;">${imageMarkup}</td>`;
      const textCell = `<td class="book-text-cell" valign="top" style="padding:0;overflow-wrap:anywhere;">${textMarkup}</td>`;
      const gap = '<td width="16" style="width:16px;font-size:0;">&nbsp;</td>';
      bookBlock.innerHTML = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;table-layout:fixed;border-collapse:collapse;border:${border ? '1px solid #d8d1c5' : '0'};"><tr><td style="padding:${border ? '12px' : '0'};"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;table-layout:fixed;"><tr>${blockLayout === "right" ? textCell + gap + coverCell : coverCell + gap + textCell}</tr></table></td></tr></table>`;
      bookBlock.querySelectorAll("img").forEach(img => {
        img.setAttribute("width", "140");
        img.style.cssText = "display:block;width:100%;max-width:140px;height:auto;border:0;margin:0;outline:none;box-shadow:none;";
        if (blockLayout === "right") img.style.marginLeft = "auto";
      });
      const titleEl = bookBlock.querySelector("h1, h2, h3, h4");
      const subtitleEl = bookBlock.querySelector("p");
      if (titleEl) {
        titleEl.style.fontSize = "24px";
        titleEl.style.fontWeight = "700";
        titleEl.style.lineHeight = "1.25";
        titleEl.style.margin = "0 0 8px 0";
      }
      if (subtitleEl) {
        subtitleEl.style.fontSize = "14px";
        subtitleEl.style.lineHeight = "1.55";
        subtitleEl.style.margin = "0 0 12px 0";
        subtitleEl.style.opacity = "0.82";
      }
    });

    // Remove composer bleed geometry, and inline essentials for mail clients.
    tempDiv.querySelectorAll("div").forEach(el => {
      if (el.style.width.includes("calc(") || parseFloat(el.style.marginLeft) < 0 || parseFloat(el.style.marginRight) < 0) {
        el.style.width = "auto"; el.style.marginLeft = "0"; el.style.marginRight = "0";
      }
      el.style.boxSizing = "border-box";
    });
    tempDiv.querySelectorAll("p").forEach(el => {
      if (!el.style.margin) el.style.margin = "0 0 12px";
      if (!el.style.lineHeight) el.style.lineHeight = "1.55";
    });
    tempDiv.querySelectorAll("img").forEach(el => {
      el.style.maxWidth = el.style.maxWidth || "100%";
      el.style.height = "auto"; el.style.display = "block";
      el.style.boxShadow = "none"; el.style.outline = "none";
    });
    tempDiv.querySelectorAll("a").forEach(el => {
      if (!el.style.color) el.style.color = accentColor;
    });
    processedBody = tempDiv.innerHTML;
  }

  return `
    <!doctype html>
    <html>
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>${escapeHtml(subject || "Newsletter")}</title>
      <style>
        body { margin:0; padding:0; background:${outerBg}; }
        table { border-collapse:collapse; }
        img { border:0; outline:none; text-decoration:none; }
        a { text-decoration:none; }

        .wrap { width:100%; background:${outerBg}; padding: 28px 12px; }
        .card {
          width: 100%;
          max-width: ${contentWidth};
          background:${cardBg};
          border-radius: 0;
          overflow: visible;
          box-shadow: 0 1px 2px rgba(0,0,0,0.06);
        }
        .inner { padding: 0 16px 18px 16px; font-family:${fontFamily}; color:${textColor}; font-size:${fontSize}; }

        .ml-body p { margin: 0 0 10px 0; line-height: 1.55; }
        .ml-body h1 { font-size: 28px; line-height: 1.2; margin: 0 0 14px 0; }
        .ml-body h2 { font-size: 22px; line-height: 1.25; margin: 14px 0 10px 0; }
        .ml-body h3 { font-size: 18px; line-height: 1.3; margin: 12px 0 8px 0; }
        .ml-body a { color: ${accentColor}; text-decoration: underline; }
        .ml-body ul, .ml-body ol { margin: 0 0 10px 22px; padding: 0; }
        .ml-body li { margin: 6px 0; }
        .ml-body img { max-width: 100%; height:auto; display:block; margin: 8px auto; }
        .ml-body img {outline: none !important;box-shadow: none !important; }
        .ml-body hr { border: none; border-top: 1px solid rgba(0,0,0,0.14); margin: 14px 0; }
        .ml-body [data-bc-section="header"] { margin-top: 0 !important; }
        .book-cover-cell img { margin: 0 !important; }

        @media (max-width: 560px){
          .wrap { padding: 18px 10px !important; }
          .inner { padding: 0 12px 14px 12px !important; }
          .book-text-cell h3 { font-size:20px !important; }
        }
      </style>
    </head>
    <body style="margin:0;padding:0;background:${outerBg};">
      ${preheaderText ? `<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;">${escapeHtml(preheaderText)}</div>` : ""}
      <table role="presentation" class="wrap" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${outerBg}" style="width:100%;background:${outerBg};border-collapse:collapse;">
        <tr>
          <td align="center">
            <table role="presentation" class="card" width="${Math.min(1000, Math.max(320, parseInt(contentWidth, 10) || 700))}" cellpadding="0" cellspacing="0" style="width:100%;max-width:${contentWidth};background:${cardBg};border-collapse:collapse;table-layout:fixed;">
              ${brandingHtml}
              <tr>
                <td class="inner" bgcolor="${cardBg}" style="padding:0 16px 18px;font-family:${fontFamily};font-size:${fontSize};color:${textColor};background:${cardBg};overflow-wrap:anywhere;">
                  <div class="ml-body">
                    ${processedBody}
                    ${footerSocialMarkup}
                    ${normalizedFooter.enabled && normalizedFooter.html ? normalizedFooter.html : ""}
                  </div>
                </td>
              </tr>
            </table>

            <div style="box-sizing:border-box;width:100%;max-width:${contentWidth}; font-family:${fontFamily}; color:#333333; background:#f6f2eb; padding:12px; font-size:12px; margin-top:14px; text-align:center;">
              You're receiving this because you subscribed.
            </div>
          </td>
        </tr>
      </table>
    </body>
    </html>
`;
}
